import { useEffect, useState } from 'react'

import { motionDuration } from '@/lib/motion/motion-duration'

/**
 * Keeps something mounted while its exit animation plays: true while `active`,
 * and for the duration named by `token` after it turns false.
 *
 * For a surface whose children go away with its state (Spotlight's body, a
 * flyout's list): without it the panel empties on the first frame of its own
 * exit. Turning active again mid-exit cancels the wait.
 */
export function useLinger(active: boolean, token: `--${string}`, fallbackMs: number): boolean {
  const [present, setPresent] = useState(active)
  // During render, so a reopen never paints a frame of "gone".
  if (active && !present) setPresent(true)

  useEffect(() => {
    if (active || !present) return
    const timer = setTimeout(
      () => {
        setPresent(false)
      },
      motionDuration(token, fallbackMs),
    )
    return () => {
      clearTimeout(timer)
    }
  }, [active, present, token, fallbackMs])

  return active || present
}
